import React from 'react';
import { UserGearIcon, XIcon } from '@phosphor-icons/react';
import Tooltip from '@/components/ui/Tooltip';
import { Specialist } from '../types';
import { getContrastTextColor } from '../utils/colorUtils';

type SpecialistPillVariant = 'button' | 'badge';

interface SpecialistPillProps {
  specialist: Specialist;
  onClick?: (event: React.MouseEvent<HTMLElement>) => void;
  /** When set, shows a dismiss control that clears the specialist without triggering onClick. */
  onDismiss?: () => void;
  /**
   * Visual variant:
   * - 'button': Larger pill with border styling, suitable for input bars
   * - 'badge': Compact badge style, suitable for message headers
   */
  variant?: SpecialistPillVariant;
  className?: string;
}

/**
 * A reusable pill component for displaying a specialist with proper contrast text color.
 * Automatically calculates text color based on the specialist's background color.
 */
export default function SpecialistPill({
  specialist,
  onClick,
  onDismiss,
  variant = 'badge',
  className = ''
}: SpecialistPillProps) {
  const textColor = getContrastTextColor(specialist.colour);
  const isDeleted = !!specialist.deleted_at;
  const interactive = !!onClick || !!onDismiss;

  const baseStyles = {
    backgroundColor: specialist.colour,
    color: textColor,
    borderColor: specialist.colour
  };

  if (variant === 'button') {
    return (
      <div
        className={`max-w-60 inline-flex items-stretch overflow-hidden rounded-lg transition-all duration-200 ${
          isDeleted ? 'opacity-70 cursor-not-allowed' : ''
        } ${className}`}
        style={baseStyles}
      >
        <button
          type="button"
          onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
          disabled={isDeleted}
          className="min-w-0 flex-1 inline-flex gap-1.5 items-center justify-left font-normal px-2.5 h-[36px] text-sm hover:opacity-90 disabled:cursor-not-allowed rounded-l-lg rounded-r-none"
          style={{ color: textColor, background: 'transparent', border: 'none' }}
          title={isDeleted ? 'This specialist has been deleted' : `Chat with ${specialist.name}`}
          aria-label={`${specialist.name} specialist`}
        >
          <span className="flex-shrink-0">
            <UserGearIcon size={18} weight="light" style={{ color: textColor }} />
          </span>
          <span className="truncate min-w-0 text-left">{specialist.name}</span>
        </button>
        {onDismiss && !isDeleted && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDismiss();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="flex-shrink-0 flex items-center justify-center px-1.5 rounded-r-lg hover:opacity-80 focus-visible:outline focus-visible:ring-2 focus-visible:ring-offset-1"
            style={{ color: textColor }}
            aria-label="Remove specialist"
            title="Remove specialist"
          >
            <XIcon size={16} weight="bold" />
          </button>
        )}
      </div>
    );
  }

  // Badge variant (default)
  const pill = interactive ? (
    <button
      onClick={onClick as React.MouseEventHandler<HTMLButtonElement>}
      disabled={isDeleted}
      className={`disabled:cursor-not-allowed disabled:opacity-70 inline-flex self-start items-center gap-2 px-2 py-1 text-sm rounded-md hover:opacity-80 transition-opacity cursor-pointer ${className}`}
      style={baseStyles}
      aria-label={`View ${specialist.name} specialist details`}
    >
      <UserGearIcon size={18} weight="light" style={{ color: textColor }} className="flex-shrink-0" />
      <span className="truncate">{specialist.name}</span>
    </button>
  ) : (
    <span
      className={`inline-flex max-w-full items-center gap-2 self-start rounded-md px-2 py-1 text-sm ${className}`}
      style={baseStyles}
    >
      <UserGearIcon size={18} weight="light" style={{ color: textColor }} className="flex-shrink-0" />
      <span className="truncate">{specialist.name}</span>
    </span>
  );

  return (
    <Tooltip title={isDeleted ? 'This specialist has been deleted.' : ''} arrow placement="top">
      {pill}
    </Tooltip>
  );
}
